import React, { useState, useEffect, useCallback } from 'react';
import HorizontalBarChart from './HorizontalBarChart/HorizontalBarChart';
import { useDebouncedResizeObserver } from '~/shared/hooks';
import { downloadHeatmapData } from '~/shared/services/api';
import styled from 'styled-components';
import { Paper, Fab } from '@mui/material';
import FilterBar from './FilterBar';
import { AlertBar, Loader } from '~/shared/components';
import { useNavAndFilters, useHeatmapData, useScroller } from '~/shared/services/charts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import IndicatorTable from './Indicator/IndicatorTable';
import IndicatorStackedAreaChart from './Indicator/IndicatorStackedAreaChart';
import { tooltipCfg } from '~/shared/config/tooltipCfg';

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  ${({ theme }: any) => `
    padding: ${theme.spacing(3)}px;
  `};
`;

const IndicatorsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const IndicatorContainer = styled.div<{ $iswide: number }>`
  min-width: calc(50% - 5px);
  ${({ $iswide }) => $iswide === 1 && 'flex: 1;'}
`;

const MainContainer = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  display: flex;
  padding: 4px;
`;

const HeatmapContainer = styled.div`
  flex: 1;
  position: relative;
  padding: 8px;
`;

const FabContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--background-chart-fab);
    color: var(--color-chart-fab);
  }
`;

const RelativeContainer = styled.div`
  position: relative;
  height: 100%;
`;

const AlignedLoader = styled(Loader)`
  position: fixed;
  left: calc(50% - 25px);
  top: calc(50% - 25px);
`;

function useHeatmapClick(navHirarchyItem: any, onScroll: any, filterValues: any) {
  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  useEffect(() => {
    setIndicatorSections([]);
  }, [navHirarchyItem, filterValues]);
  const onClick = useCallback(
    (cellInfo: { source: string; topic?: string; symbol?: string }) => {
      setIndicatorSections([]);
      setTimeout(
        () =>
          navHirarchyItem &&
          filterValues &&
          setIndicatorSections(
            navHirarchyItem?.indicators[cellInfo?.source]?.map((indicatorObj: any) => {
              const indicator = indicatorObj && indicatorObj[0];
              const type = indicatorObj && indicatorObj[1];
              return { indicator, cellInfo, type };
            }),
          ),
        10,
      );
      setTimeout(onScroll, 200);
    },
    [navHirarchyItem, filterValues, onScroll],
  );
  return { indicatorSections, onClick };
}

export default function HorizontalBarChartPage({
  navHirarchy,
  gridIndex,
  gridHeight,
  onGridClick,
  gridFilterValues,
  gridFilterValuesID,
  gridPrism,
}: any) {
  const { scrollerRef, scrollToTop, scrollToBottom } = useScroller();
  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);

  const { data, error, isLoading, colorScheme, chartTitle, chartSubtitle, chartYtitle, chartYmax, chartYmin } =
    useHeatmapData(category, subcategory, subsubcategory, prism, gridFilterValuesID || filterValuesID, prismId);

  const { size, ref: chartSizeRef }: any = useDebouncedResizeObserver(100);

  const { indicatorSections, onClick } = useHeatmapClick(
    navHirarchyItem,
    scrollToBottom,
    gridFilterValues || filterValues,
  );

  const handleOnGridClick = useCallback(
    (props: any) => {
      onGridClick({
        ...props,
        navHirarchyItem,
        filterValues: gridFilterValues || filterValues,
        category,
        subcategory,
        subsubcategory,
        prism,
        prismId,
        indicatorSections,
      });
    },
    [
      navHirarchyItem,
      filterValues,
      gridFilterValues,
      category,
      subcategory,
      subsubcategory,
      prism,
      prismId,
      indicatorSections,
      onGridClick,
    ],
  );

  const onDownload = () =>
    downloadHeatmapData(
      category,
      subcategory,
      subsubcategory,
      prism,
      gridFilterValuesID || filterValuesID,
      prismId,
      chartTitle,
    ).catch(e => {
      alert(e.message);
    });

  return (
    <ScrollWrap ref={scrollerRef}>
      {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      <MainContainer>
        <ContentContainer>
          <HeatmapContainer ref={chartSizeRef}>
            <RelativeContainer>
              {isLoading ? (
                <AlignedLoader />
              ) : (
                data && (
                  <HorizontalBarChart
                    {...{
                      data,
                      size,
                      onClick: gridIndex || gridIndex === 0 ? handleOnGridClick : onClick,
                      prism,
                      filterValues: gridFilterValues || filterValues,
                      colorScheme,
                      chartTitle,
                      chartSubtitle,
                      chartYtitle,
                      tooltipCfg,
                      gridHeight,
                      gridIndex,
                      gridPrism,
                      chartYmax,
                      chartYmin,
                    }}
                  />
                )
              )}
            </RelativeContainer>
          </HeatmapContainer>
          {!gridIndex && gridIndex !== 0 && (
            <FilterBar
              type={navHirarchyItem?.type}
              filters={navHirarchyItem?.filters}
              values={gridFilterValues || filterValues}
              filterHierarchy={navHirarchyItem?.narrativeFilterTree}
              filterHierarchyValues={gridFilterValuesID || filterValuesID}
              onDownload={onDownload}
              tooltipCfg={tooltipCfg}
            />
          )}
        </ContentContainer>
      </MainContainer>
      <IndicatorsContainer>
        {!gridIndex &&
          gridIndex !== 0 &&
          indicatorSections.map((section: any, index: number) => (
            <IndicatorContainer
              key={'container' + section.indicator + section.topic + section.symbol + String(index)}
              $iswide={indicatorSections.length === index + 1 && indicatorSections.length % 2 !== 0 ? 1 : 0}
            >
              {section.type === 'bar' && (
                <IndicatorStackedAreaChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorStackedAreaChart>
              )}
              {section.type === 'line' && (
                <IndicatorLineChart
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorLineChart>
              )}
              {section.type === 'table' && (
                <IndicatorTable
                  key={section.indicator + section.topic + section.symbol + String(index)}
                  {...{
                    category,
                    prism,
                    prismId,
                    ...section,
                    filterValues: gridFilterValues || filterValues,
                    tooltipCfg,
                  }}
                ></IndicatorTable>
              )}
            </IndicatorContainer>
          ))}
      </IndicatorsContainer>
      <FabContainer>
        {indicatorSections.length > 0 && (
          <Fab onClick={scrollToTop}>
            <ExpandLess />
          </Fab>
        )}
      </FabContainer>
    </ScrollWrap>
  );
}
